import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormArray,
  AbstractControl,
} from '@angular/forms';

import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import { TuiAlertService } from '@taiga-ui/core';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';

import { ClientService } from 'src/app/services/client/client.service';
import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';

import {
  strictEmailValidator,
  ibanValidator,
  swiftValidator,
} from 'src/app/validators/form-validators';
import {
  ClientPaymentMethods,
  clientTypeOptions,
  contactDepartmentsTypeOptions,
  paymentMethods,
} from 'src/app/enum/client-enums';
import { PERFILS } from 'src/app/constants/client-constants';
import { EXPENSE_CATEGORIES } from 'src/app/pages/expenses/enums/expense-category.enum';
import { EXPENSE_CATEGORY_LABEL } from 'src/app/pages/expenses/constants/expense-category.map';
import { phoneValidator } from 'src/app/utils/phone-validator';
import {
  BusinessUser,
  ClientBillingData,
  CreateNewClientRequest,
  GetClientDetailsResponse,
  UpdateClientRequest,
} from 'src/app/models/client/client.model';
import { Zone, ZonesResponse } from 'src/app/models/zone/zone.model';
import { clientStatus } from 'src/app/constants/data';
import { CommonSelectorV4 } from 'src/app/models/common/common.model';
import { TranslateService } from '@ngx-translate/core';
import { phoneValidatorV2 } from 'src/app/utils/phone-validator-v2';
import { phoneExamples, splitPhone } from 'src/app/utils/phone-utils';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';

declare const google: any;

@Component({
  selector: 'app-modal-add-client',
  templateUrl: './modal-add-client.component.html',
  styleUrls: ['./modal-add-client.component.css'],
})
export class ModalAddClientComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() action!: 'ad' | 'ed';
  @Input() client!: BusinessUser;
  @Input() clientDetails!: GetClientDetailsResponse;

  @ViewChild('addressBilling', { static: true })
  addressBillingInput!: ElementRef<HTMLInputElement>;

  readonly statusList = clientStatus;
  readonly perfils = PERFILS;
  readonly paymentMethods = paymentMethods;
  readonly contactDepartments = contactDepartmentsTypeOptions;
  readonly clientTypeOptions = clientTypeOptions;

  /** Catálogo de gastos, compartido con la ventana de Gastos para no tener dos listas que mantener. */
  readonly expenseCategories = EXPENSE_CATEGORIES;
  readonly expenseCategoryLabels = EXPENSE_CATEGORY_LABEL;

  readonly countryCodes = [
    TuiCountryIsoCode.ES,
    TuiCountryIsoCode.GB,
    TuiCountryIsoCode.FR,
    TuiCountryIsoCode.IT,
    TuiCountryIsoCode.DE,
    TuiCountryIsoCode.PT,
    TuiCountryIsoCode.NL,
    TuiCountryIsoCode.AD,
    TuiCountryIsoCode.CL,
    TuiCountryIsoCode.DO,
  ] as const;

  phoneExample$!: Observable<string>;
  payPhoneExample$!: Observable<string>;
  contactPlaceholders$: Observable<string>[] = [];

  clientForm: FormGroup;
  get contactsArray(): FormArray {
    return this.clientForm.get('contacts') as FormArray;
  }

  get rebilledExpensesArray(): FormArray {
    return this.clientForm.get('rebilledExpenseCategories') as FormArray;
  }

  /**
   * Categorías desmarcadas, que son las que NO se le repercuten al cliente.
   *
   * Se manda lo excluido y no lo incluido a propósito: así un cliente al que no se le toca nada
   * viaja con la lista vacía y se comporta igual que antes de que existiera esta configuración.
   */
  private get nonRebilledExpenseCategories(): string[] {
    return this.expenseCategories.filter(
      (_, index) => !this.rebilledExpensesArray.at(index).value
    );
  }

  zones: Zone[] = [];

  isSaving: boolean = false;
  loadingZones: boolean = true;
  openElement: EventTarget | string | null = null;

  statusesTranslated: CommonSelectorV4[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private ngZone: NgZone,
    private offcanvas: NgbOffcanvas,
    private alerts: TuiAlertService,
    private clientSvc: ClientService,
    private historySvc: HistoryServicesService,
    private translate: TranslateService,
    private host: ElementRef<HTMLElement>
  ) {
    this.clientForm = this.buildForm();
    this.setupIbanSwiftConditionalValidation();
  }

  ngOnInit(): void {
    this.translateStatus();
    this.loadZones();
    this.initPhonePlaceholders();
    this.onChangeStatusToInactive();
  }

  onChangeStatusToInactive(): void {
    this.clientForm
      .get('status')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((isActive: boolean) => {
        if (isActive === false) {
          this.alerts
            .open(
              this.translate.instant(
                'modals.add_client.warning_clientInactive'
              ),
              { status: 'warning', autoClose: 5000 }
            )
            .subscribe();
        }
      });
  }

  private initPhonePlaceholders(): void {
    const officeIsoCtrl = this.clientForm.get('officePhoneIso')!;
    this.phoneExample$ = officeIsoCtrl.valueChanges.pipe(
      startWith(officeIsoCtrl.value),
      map(
        (iso: TuiCountryIsoCode | null) =>
          phoneExamples[iso as TuiCountryIsoCode] ?? '+00 000 000 000'
      )
    );

    const payIsoCtrl = this.clientForm.get('paymentContactPhoneIso')!;
    this.payPhoneExample$ = payIsoCtrl.valueChanges.pipe(
      startWith(payIsoCtrl.value),
      map(
        (iso: TuiCountryIsoCode | null) =>
          phoneExamples[iso as TuiCountryIsoCode] ?? '+00 000 000 000'
      )
    );
  }

  onLimitLength(event: Event, max: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    if (val.length > max) {
      const trimmed = val.slice(0, max);
      input.value = trimmed;
      this.clientForm.get(controlName)?.setValue(trimmed, { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    if (!google?.maps?.places) {
      console.error('Google Maps Places API not loaded');
      return;
    }
    this.setupAutocomplete(
      this.addressBillingInput.nativeElement,
      'billingAddress',
      true
    );
  }

  private setupAutocomplete(
    element: HTMLInputElement,
    controlName: 'billingAddress',
    includeCoords: boolean
  ): void {
    const autocomplete = new google.maps.places.Autocomplete(element, {
      types: ['address'],
      fields: ['formatted_address', 'address_components', 'geometry'],
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = autocomplete.getPlace();

        if (!place.geometry) {
          console.error('Selected place has no geometry');
          return;
        }

        const comps = place.address_components || [];
        const find = (type: string) =>
          comps.find((c: any) => c.types.includes(type))?.long_name || '';

        const patch: any = {
          [controlName]: place.formatted_address ?? '',
          postalCode: find('postal_code'),
          city: find('locality'),
          province: find('administrative_area_level_2'),
          country: find('country'),
        };

        this.clientForm.patchValue(patch, { emitEvent: false });
      });
    });
  }

  private translateStatus(): void {
    this.statusesTranslated = this.statusList.map((s) => ({
      name: this.translate.instant(s.name),
      value: s.value,
    }));
  }

  private buildForm(): FormGroup {
    return this.fb.group(
      {
        zone: [null, Validators.required],
        companyName: ['', Validators.required],
        keyAccount: ['', Validators.required],
        status: [null, Validators.required],
        type: [null, Validators.required],
        group: [''],
        subGroup: [''],
        additionalInfo: [''],
        name: ['', Validators.required],
        lastName: ['', Validators.required],
        officePhoneIso: [TuiCountryIsoCode.ES],
        officePhone: [
          '',
          [Validators.required, phoneValidatorV2('officePhoneIso')],
        ],
        corporateEmail: ['', [Validators.required, strictEmailValidator]],
        billingName: ['', Validators.required],
        billingCIF: this.fb.control('', {
          validators: [
            Validators.required,
            Validators.minLength(5),
            Validators.maxLength(20),
          ],
          updateOn: 'change',
        }),
        billingAddress: ['', Validators.required],
        postalCode: ['', [Validators.required]],
        city: ['', Validators.required],
        province: ['', Validators.required],
        country: ['', Validators.required],
        billingDetails: [''],
        paymentMethod: [null, Validators.required],
        iban: [''],
        swift: [''],
        paymentContactName: ['', Validators.required],
        paymentContactLastName: ['', Validators.required],
        paymentContactEmail: ['', [Validators.required, strictEmailValidator]],
        paymentContactPhoneIso: [TuiCountryIsoCode.ES],
        paymentContactPhone: [
          '',
          [Validators.required, phoneValidatorV2('paymentContactPhoneIso')],
        ],
        contacts: this.fb.array([]),
        // Un control por categoría, todas marcadas: por defecto se le repercute todo al cliente.
        rebilledExpenseCategories: this.fb.array(
          EXPENSE_CATEGORIES.map(() => this.fb.control(true))
        ),
      },
      {
        updateOn: 'blur',
        validators: this.ibanSwiftRequired,
      }
    );
  }

  private debugFormValidity(ctrl: AbstractControl, path = ''): void {
    if ((ctrl as FormGroup | FormArray).controls) {
      const children =
        ctrl instanceof FormArray
          ? (ctrl as FormArray).controls
          : (ctrl as FormGroup).controls;

      Object.entries(children).forEach(([key, childCtrl], idx) => {
        const childPath =
          ctrl instanceof FormArray ? `${path}[${idx}]` : `${path}.${key}`;
        this.debugFormValidity(childCtrl, childPath);
      });
    }

    if (ctrl.invalid) {
      console.warn(
        `%c ${path || '(root)'} → ${ctrl.status}`,
        'color:#e53935;font-weight:600'
      );
      console.table(ctrl.errors || {});
    }
  }

  private setupIbanSwiftConditionalValidation(): void {
    const pmCtrl = this.clientForm.get('paymentMethod')!;
    const ibanCtrl = this.clientForm.get('iban')!;
    const swiftCtrl = this.clientForm.get('swift')!;

    pmCtrl.valueChanges.subscribe((method: number) => {
      if (method === ClientPaymentMethods.directDebit) {
        ibanCtrl.setValidators([
          Validators.required,
          Validators.minLength(15),
          Validators.maxLength(34),
          ibanValidator,
        ]);
        swiftCtrl.setValidators([
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(11),
          swiftValidator,
        ]);
      } else {
        ibanCtrl.clearValidators();
        swiftCtrl.clearValidators();
      }

      ibanCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      swiftCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    });
  }

  private ibanSwiftRequired = (ctrl: AbstractControl) => {
    const method = ctrl.get('paymentMethod')?.value;
    const iban = ctrl.get('iban')?.value;
    const swift = ctrl.get('swift')?.value;

    return method === ClientPaymentMethods.directDebit && (!iban || !swift)
      ? { ibanSwiftRequired: true }
      : null;
  };

  private loadZones(): void {
    this.historySvc.getZones().subscribe({
      next: (zones: ZonesResponse) => {
        this.zones = zones.zones;
        this.loadingZones = false;
        if (this.action === 'ed' && this.clientDetails) {
          this.populateForm(this.clientDetails);
        }
      },
      error: (error) => {
        this.loadingZones = false;
        this.zones = [];
      },
    });
  }

  saveClient(): void {
    if (this.clientForm.invalid) {
      this.alerts
        .open(
          this.translate.instant(
            'comercial.clients.modal-add-client.error_formInvalid'
          ),
          { status: 'error', autoClose: 3000 }
        )
        .subscribe();

      this.clientForm.markAllAsTouched();
      setTimeout(() => {
        const firstInvalid: HTMLElement | null =
          this.host.nativeElement.querySelector('.ng-invalid');
        if (firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (firstInvalid as HTMLElement).focus?.();
        }
      });
      return;
    }

    this.isSaving = true;

    if (this.action === 'ad') {
      const req = this.buildCreateRequest();
      this.historySvc.addClient(req).subscribe({
        next: (response) => {
          if (response.wasSuccessful) {
            this.handleSuccess();
            this.isSaving = false;
          } else {
            this.isSaving = false;
            this.handleError();
          }
        },
        error: (err) => {
          this.handleError(err);
          this.isSaving = false;
        },
      });
    } else {
      const req = this.buildUpdateRequest();
      this.historySvc.updateClient(req).subscribe({
        next: (response) => {
          if (response.isSuccess) {
            this.handleSuccess();
            this.isSaving = false;
          } else {
            this.isSaving = false;
            this.handleError();
          }
        },
        error: (err) => {
          this.handleError(err);
          this.isSaving = false;
        },
      });
    }
  }

  formatIban(event: any): void {
    let value = event.target.value.replace(/\s+/g, '').toUpperCase();
    value = value.replace(/(.{4})/g, '$1 ').trim();
    this.clientForm.get('iban')?.setValue(value, { emitEvent: false });
  }

  openPhoneContainer(clickEvent: MouseEvent): void {
    const clickedElement = clickEvent.currentTarget as HTMLElement;

    if (clickedElement === this.openElement) {
      this.openElement = null;
    }

    if (clickedElement !== this.openElement) {
      const dropdownElements: HTMLCollectionOf<HTMLElement> =
        document.getElementsByTagName(
          'tui-dropdown'
        ) as HTMLCollectionOf<HTMLElement>;

      if (dropdownElements.length > 0 && dropdownElements[0] !== undefined) {
        const foundDropdownElement: HTMLElement = dropdownElements[0];
        const targetContainer: HTMLElement | null = document.getElementById(
          'modal_show_countries_phones'
        );

        if (targetContainer) {
          targetContainer.innerHTML = '';
          targetContainer.append(foundDropdownElement);
        } else {
          console.warn(
            "El contenedor 'modal_show_countries_phones' no fue encontrado en el DOM."
          );
        }
      } else {
        console.warn("No se encontraron elementos 'tui-dropdown' en el DOM.");
      }
      this.openElement = clickedElement;
    } else {
      this.openElement = null;
    }
  }

  private buildCreateRequest(): CreateNewClientRequest {
    const f = this.clientForm.value;

    return {
      /* ─── Datos generales ─── */
      operativeZoneGuid: this.lookupZoneId(f.zone),
      clientCompanyName: f.companyName,
      keyAccountManagerName: f.keyAccount,
      isClientActive: f.status,
      clientType: f.type,
      clientGroup: f.group,
      clientSubgroup: f.subGroup,
      additionalInformation: f.additionalInfo,

      /* ─── Contacto principal ─── */
      clientIdentityData: {
        firstName: f.name,
        lastName: f.lastName,
        emailAddress: f.corporateEmail,
        phoneNumber: f.officePhone,
      },

      /* ─── Contactos adicionales ─── */
      additionalContacts: this.contactsArray.value.map((c: any) => ({
        firstName: c.firstName,
        lastName: c.lastName,
        emailAddress: c.contactEmailAddress,
        clientContactType: c.department,
        phoneNumber: c.phoneNumber,
      })),

      /* ─── Información de facturación ─── */
      billingInformation: {
        billingName: f.billingName,
        fiscalCode: f.billingCIF,
        billingAddress: f.billingAddress,
        billingAddressStreet: f.billingAddress,
        billingAddressCity: f.city,
        billingAddressPostalCode: f.postalCode,
        billingAddressState: f.province,
        billingAddressCountry: f.country,
        billingAddressAdditionalDetails: f.billingDetails,

        /* Datos bancarios */
        iban: f.iban,
        swift: f.swift,
        paymentMethod: f.paymentMethod,

        /* Contacto de facturación/pago */
        billingContact: {
          firstName: f.paymentContactName,
          lastName: f.paymentContactLastName,
          emailAddress: f.paymentContactEmail,
          clientContactType: ClientPaymentMethods.directDebit,
          phoneNumber: f.paymentContactPhone,
        },

        /* Repercusión de gastos */
        nonRebilledExpenseCategories: this.nonRebilledExpenseCategories,
      },
    };
  }

  private buildUpdateRequest(): UpdateClientRequest {
    const f = this.clientForm.value;

    const base = this.clientDetails!;

    /* ─────────────────────────────────────── */

    return {
      /* ─── Identificadores ─── */
      clientGuid: base.clientGuid,
      operativeZoneGuid: this.lookupZoneId(f.zone),

      /* ─── Datos generales ─── */
      clientCompanyName: f.companyName,
      managerKeyAccount: f.keyAccount,
      isClientActive: f.status,
      clientType: f.type,
      clientGroup: f.group,
      clientSubgroup: f.subGroup,
      additionalInformation: f.additionalInfo,

      /* ─── Contacto principal ─── */
      contactData: {
        contactGuid: base.contactData.contactGuid,
        name: f.name,
        surnames: f.lastName,
        email: f.corporateEmail,
        phoneNumber: f.officePhone,
      },

      /* ─── Contactos adicionales ─── */
      additionalContactsData: this.contactsArray.value.map((c: any) => ({
        contactGuid: c.contactGuid,
        name: c.firstName,
        surnames: c.lastName,
        email: c.contactEmailAddress,
        phoneNumber: c.phoneNumber,
        department: c.department,
      })),

      /* ─── Información de facturación ─── */
      billingData: {
        ...base.billingData,
        clientBillingName: f.billingName,
        fiscalCode: f.billingCIF,
        billingAddress: f.billingAddress,
        postalCode: f.postalCode,
        city: f.city,
        state: f.province,
        country: f.country,
        additionalDetails: f.billingDetails,

        /* Datos bancarios */
        iban: f.iban,
        swift: f.swift,
        paymentMethod: f.paymentMethod,

        /* Contacto de facturación */
        billingContact: {
          ...base.billingData.billingContact,
          name: f.paymentContactName,
          surnames: f.paymentContactLastName,
          email: f.paymentContactEmail,
          phoneNumber: f.paymentContactPhone,
        },

        /* Repercusión de gastos */
        nonRebilledExpenseCategories: this.nonRebilledExpenseCategories,
      } as ClientBillingData,
    };
  }

  private lookupZoneId(name: string): string {
    return this.zones.find((z) => z.zoneName === name)?.zoneId ?? '';
  }

  private populateForm(d: GetClientDetailsResponse): void {
    this.contactsArray.clear();

    // ── Teléfono principal ──────────────────────────────────
    const principalPh = splitPhone(d.contactData.phoneNumber);

    // ── Teléfono de pago/facturación ────────────────────────
    const payPh = splitPhone(d.billingData.billingContact.phoneNumber);

    const patch: any = {
      /* === Datos generales ================================ */
      zone:
        this.zones.find((z) => z.zoneId === d.operativeZoneGuid)?.zoneName ??
        null,
      companyName: d.clientCompanyName,
      keyAccount: d.managerKeyAccount,
      status: d.isClientActive,
      type: d.clientType,
      group: d.clientGroup,
      subGroup: d.clientSubgroup,
      additionalInfo: d.additionalInformation,

      /* === Contacto principal ============================ */
      name: d.contactData.name,
      lastName: d.contactData.surnames,
      officePhoneIso: principalPh.iso,
      officePhone: principalPh.national,
      corporateEmail: d.contactData.email,

      /* === Facturación =================================== */
      billingName: d.billingData.clientBillingName,
      billingCIF: d.billingData.fiscalCode,
      billingAddress: d.billingData.billingAddress,
      postalCode: d.billingData.postalCode,
      city: d.billingData.city,
      province: d.billingData.state,
      country: d.billingData.country,
      billingDetails: d.billingData.additionalDetails,

      /* === Bancarios ===================================== */
      iban: d.billingData.iban,
      swift: d.billingData.swift,

      /* === Contacto de pago ============================== */
      paymentContactName: d.billingData.billingContact.name,
      paymentContactLastName: d.billingData.billingContact.surnames,
      paymentContactEmail: d.billingData.billingContact.email,
      paymentContactIso: payPh.iso,
      paymentContactPhone: payPh.national,
    };

    /* Método de pago solo si está entre los permitidos */
    const incoming = d.billingData.paymentMethod;
    patch.paymentMethod = this.paymentMethods.some((m) => m.value === incoming)
      ? incoming
      : null;

    this.clientForm.patchValue(patch, { emitEvent: false });

    /* === Repercusión de gastos =========================== */
    // Del back llegan las categorías EXCLUIDAS, así que se marca todo lo demás.
    const nonRebilled = d.billingData.nonRebilledExpenseCategories ?? [];
    this.expenseCategories.forEach((category, index) => {
      this.rebilledExpensesArray
        .at(index)
        .setValue(!nonRebilled.includes(category), { emitEvent: false });
    });

    /* === Contactos adicionales =========================== */
    d.additionalContactsData?.forEach((c) => {
      const ph = splitPhone(c.phoneNumber);
      this.addContact({
        firstName: c.name,
        lastName: c.surnames,
        contactEmailAddress: c.email,
        phoneIso: ph.iso,
        phoneNumber: ph.national,
        department: c.department,
        contactGuid: c.contactGuid,
      });
    });
  }

  addContact(
    preset: Partial<{
      firstName: string;
      lastName: string;
      contactEmailAddress: string;
      phoneNumber: string;
      phoneIso: TuiCountryIsoCode;
      department: number | null;
      contactGuid: string;
    }> = {}
  ): FormGroup {
    const phoneParts = splitPhone(preset.phoneNumber ?? '');

    const contactFG = this.fb.nonNullable.group({
      /* ▶ Datos personales ─────────────────────────────── */
      firstName: [preset.firstName ?? '', Validators.required],
      lastName: [preset.lastName ?? '', Validators.required],
      contactEmailAddress: [
        preset.contactEmailAddress ?? '',
        [Validators.required, strictEmailValidator],
      ],

      /* ▶ Teléfono + país ───────────────────────────────── */
      phoneIso: [preset.phoneIso ?? phoneParts.iso],
      phoneNumber: [
        phoneParts.national,
        [Validators.required, phoneValidatorV2('phoneIso')],
      ],

      /* ▶ Otros ─────────────────────────────────────────── */
      department: [preset.department ?? null, Validators.required],
      contactGuid: [preset.contactGuid ?? ''],
    });

    const placeholder$ = contactFG.get('phoneIso')!.valueChanges.pipe(
      startWith(contactFG.get('phoneIso')!.value),
      map((iso: TuiCountryIsoCode) => phoneExamples[iso] ?? '+00 000 000 000')
    ) as Observable<string>;

    this.contactPlaceholders$.push(placeholder$);

    this.contactsArray.push(contactFG);
    return contactFG;
  }

  removeContact(i: number): void {
    this.contactsArray.removeAt(i);
  }

  private handleSuccess(): void {
    const key =
      this.action === 'ad'
        ? 'comercial.clients.modal-add-client.success_clientAdded'
        : 'comercial.clients.modal-add-client.success_clientUpdated';

    this.alerts
      .open(this.translate.instant(key), {
        status: 'success',
        autoClose: 3000,
      })
      .subscribe();

    this.offcanvas.dismiss();
    this.clientSvc.notifyClientCreated();
  }

  private handleError(err?: any): void {
    const isServerError = err?.status === 500;
    const msgKey = isServerError
      ? 'comercial.clients.modal-add-client.error_server'
      : 'comercial.clients.modal-add-client.error_unknown';

    const baseMessage = this.translate.instant(msgKey);
    const message = isServerError
      ? baseMessage
      : err?.message
      ? `${baseMessage}: ${err.message}`
      : baseMessage;

    this.alerts.open(message, { status: 'error', autoClose: 3000 }).subscribe();
  }

  requiresBankData(method: number): boolean {
    return method === ClientPaymentMethods.directDebit;
  }

  closeCanvas(): void {
    this.offcanvas.dismiss();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
